import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem } from '../patterns';
import { nutritionSessionService } from '../../services/nutritionSessionService';
import { NutritionProgramme } from '../../services/nutritionService';
import './nutrition.css';

interface Props {
  programName: string;
  programmeId: string;
  programmeStatus: NutritionProgramme['status'];
  /**
   * Omit to drop the "View details" item entirely. A call site that is ALREADY
   * on the programme's detail page (FoliarProgrammeEditorPage) has nowhere to
   * send the user, and a no-op menu item that silently does nothing is worse
   * than no item at all.
   */
  onViewDetails?: () => void;
  onClone: () => void;
  onDelete: () => void;
  onRefresh?: () => void;
  /**
   * Disables the Delete item. Driven by the CALLER's own lock decision
   * (e.g. FoliarProgrammeEditorPage's `locked = programme.status === 'active'`)
   * rather than recomputed here from `programmeStatus` — so a page that
   * derives `locked` once and reuses it for the warn Notice, disabled inputs,
   * AND this menu stays byte-identical across all three. Defaults to false so
   * existing list-page callers (which never pass it) keep today's behaviour.
   */
  deleteDisabled?: boolean;
}

/**
 * Program-row 3-dot menu for the Nutrition Programs list. Mirrors the
 * Protection ProgramActionsMenu but scoped to Nutrition actions.
 *
 * Slice 3: adds "Unlock" for active/locked programmes (calls
 * unlockProgramme; handles 409 has-sessions gracefully).
 */
const NutritionProgramActionsMenu: React.FC<Props> = ({
  programName,
  programmeId,
  programmeStatus,
  onViewDetails,
  onClone,
  onDelete,
  onRefresh,
  deleteDisabled,
}) => {
  const { t } = useTranslation();
  const [unlockError, setUnlockError] = useState<string | null>(null);

  const canUnlock = programmeStatus === 'active' || programmeStatus === 'reviewed';

  const handleUnlock = async () => {
    setUnlockError(null);
    try {
      await nutritionSessionService.unlockProgramme(programmeId);
      onRefresh?.();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setUnlockError(t('nutrition.sessions.unlockError409'));
      } else {
        setUnlockError(t('nutrition.sessions.unlockError'));
      }
    }
  };

  return (
    <>
      {unlockError && (
        <div className="ntr-error" role="alert" style={{ fontSize: 12, marginTop: 4 }}>
          {unlockError}
        </div>
      )}
      <Menu
        placement="bottom-end"
        trigger={
          <button
            type="button"
            className="pat-menu__trigger"
            data-testid="nutrition-program-actions-toggle"
            aria-label={t('nutrition.programme.list.actionsMenu', { name: programName })}
          >
            &#8942;
          </button>
        }
      >
        {onViewDetails && (
          <MenuItem onClick={onViewDetails}>{t('nutrition.programme.list.viewDetails')}</MenuItem>
        )}
        <MenuItem onClick={onClone}>{t('nutrition.programme.list.clone')}</MenuItem>
        {canUnlock && (
          <MenuItem onClick={handleUnlock}>{t('nutrition.sessions.unlock')}</MenuItem>
        )}
        <MenuItem onClick={onDelete} tone="danger" disabled={deleteDisabled}>{t('common.delete')}</MenuItem>
      </Menu>
    </>
  );
};

export default NutritionProgramActionsMenu;
